import type { TFunction } from 'i18next';

import { normalizeCurrencyCodes } from '@/lib/currency/currencyOptionsKeys';
import type { BulkEditColumnId } from './bulk-edit.types';

export { normalizeCurrencyCodes };

export interface BulkEditStoreCurrencies {
  presentmentCurrencies: string[];
  chargeCurrencies: string[];
  baseCurrency: string;
  /** Unique currencies for price editing (presentment first, then charge-only). */
  allPriceCurrencies: string[];
}

export function buildBulkEditStoreCurrencies(
  presentmentCurrencies: string[] | undefined | null,
  chargeCurrencies: string[] | undefined | null,
  baseCurrencyFallback?: string | null,
): BulkEditStoreCurrencies {
  const presentment = normalizeCurrencyCodes(presentmentCurrencies);
  const charge = normalizeCurrencyCodes(chargeCurrencies);
  const baseCurrency =
    baseCurrencyFallback?.toUpperCase() || charge[0] || presentment[0] || 'USD';

  const chargeOnly = charge.filter((currency) => !presentment.includes(currency));
  const allPriceCurrencies = [...presentment, ...chargeOnly];

  if (allPriceCurrencies.length === 0) {
    allPriceCurrencies.push(baseCurrency);
  }

  return {
    presentmentCurrencies: presentment.length > 0 ? presentment : [baseCurrency],
    chargeCurrencies: charge.length > 0 ? charge : [baseCurrency],
    baseCurrency,
    allPriceCurrencies,
  };
}

/** @deprecated Legacy dynamic ids `price:USD` — stripped by normalizeVisibleColumns */
export function isLegacyPriceColumnId(column: string): column is `price:${string}` {
  return column.startsWith('price:');
}

export function normalizeVisibleColumns(columns: Set<string>): Set<BulkEditColumnId> {
  const next = new Set<BulkEditColumnId>();
  for (const id of columns) {
    if (isLegacyPriceColumnId(id)) continue;
    next.add(id as BulkEditColumnId);
  }
  next.add('price');
  return next;
}

export function getPriceColumnRole(
  currency: string,
  storeCurrencies: BulkEditStoreCurrencies,
): 'display' | 'charge' | 'both' {
  const upper = currency.toUpperCase();
  const inPresentment = storeCurrencies.presentmentCurrencies.includes(upper);
  const inCharge = storeCurrencies.chargeCurrencies.includes(upper);
  if (inPresentment && inCharge) return 'both';
  if (inPresentment) return 'display';
  return 'charge';
}
